import {
  vorlaufBietetAn,
  arbeitszeitVon,
  feiertagName,
  fensterDesTages,
  fensterKuerzen,
  hatEigeneZeit,
  tagesabwesenheit,
  wirksamerMarkierTag,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import {
  abwesenheitenQuery,
  meineArbeitszeitQuery,
  poolsAllerWeltenQuery,
  termineAmTagQuery,
  workspacesQuery,
} from '../../lib/queries'
import { weltdauern } from '../../lib/dauer'
import { useMarkierUhrzeiten, useOffeneMarkierungen } from '../../lib/markierung'
import { useAufgabenAllerWelten } from '../../lib/welt'
import { useWirksameTermine } from '../../lib/termine'
import { useMeineAufgaben } from './bestand'
import { tagBelegen, type FesteAufgabe, type PlanAufgabe } from './tagesplan'

/** `2026-09-30` aus einem Datum – in Ortszeit, nicht UTC (sonst springt der Tag um Mitternacht). */
export const tagesschluesselVon = (d: Date) =>
  new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(d)

/**
 * **Der Plan eines Tages** – herausgelöst aus dem Kalender-Modus
 * ([Z-24](../../../../../docs/phasen/zusatz-24-my-work.md) AP 24.2), damit „My Work" und
 * `TagesplanAnsicht` dieselbe Rechnung benutzen: was in Arbeit ist, was für den Tag markiert ist,
 * was der Vorlauf als Vorschlag anbietet – gelegt von {@link tagBelegen} in die Lücken zwischen
 * den Outlook-Terminen.
 *
 * `takt` geht nur in die Abhängigkeiten ein: Wer ihn (z. B. jede Minute) erhöht, bekommt den Plan
 * mit der aktuellen Uhr neu gerechnet. Der Kalender-Modus lässt ihn weg und rechnet wie bisher.
 */
export function useTagesplan(tag: string, takt = 0) {
  const { aufgaben, ichId } = useMeineAufgaben()
  /**
   * **Alle sichtbaren Aufgaben** – für das, was ich markiert habe. Markieren ist nicht nehmen
   * ([F-97](../../../../../docs/OFFENE-FRAGEN.md#f-97)): eine markierte Pool-Aufgabe gehört mir
   * noch nicht und stünde in `useMeineAufgaben` nicht drin.
   */
  const { aufgaben: bestand } = useAufgabenAllerWelten()
  const markierungen = useOffeneMarkierungen()
  /** Was an eine Uhrzeit gezogen wurde (13.10.2026) – `taskId → { am, um }`. */
  const uhrzeiten = useMarkierUhrzeiten()
  /** Z-20: der wirksame Termin je Aufgabe – dieselbe Rechnung wie im Vorlauf. */
  const termine = useWirksameTermine()
  const { data: kalender } = useQuery(termineAmTagQuery(tag))
  const { data: zeitFelder } = useQuery(meineArbeitszeitQuery)
  const { data: welten = [] } = useQuery(workspacesQuery)
  const { data: allePools = [] } = useQuery(poolsAllerWeltenQuery)
  const heute = tagesschluesselVon(new Date())
  /**
   * **Die eigene Arbeitszeit statt des Hausmaßes** (AP 19.4). Solange die Abfrage läuft, gilt
   * das Hausmaß – `arbeitszeitVon(undefined)` liefert es. Besser ein Raster, das sich einmal
   * verschiebt, als eins, das gar nicht da ist.
   */
  const zeit = arbeitszeitVon(zeitFelder)

  /**
   * **Die eigenen Abwesenheiten** (AP 19.5) – aus der Liste, die „Mein Tag" ohnehin schon lädt.
   *
   * `absences.uebersicht` liefert −45 bis +120 Tage für **alle** in einem Aufruf (Leitplanke 2),
   * und die Zeile „14 heute nicht da" oben auf derselben Seite lebt bereits davon. Für den Plan
   * war deshalb keine zusätzliche Abfrage nötig – nur der Blick auf die eigenen Zeilen.
   */
  const { data: abwesenheitsDaten } = useQuery(abwesenheitenQuery)
  const meineAbwesenheiten = useMemo(
    () => (abwesenheitsDaten?.abwesenheiten ?? []).filter((a) => a.personId === ichId),
    [abwesenheitsDaten, ichId],
  )

  /**
   * **Ist das überhaupt ein Arbeitstag?** Samstag, Sonntag und die gesetzlichen Feiertage in
   * NRW ([`feiertage.ts`](../../../../../packages/shared/src/feiertage.ts)) sind keiner. Vor dem
   * Blättern fiel das nicht auf – heute ist immer heute; wer vorblättert, landet unweigerlich
   * irgendwann auf einem Samstag.
   */
  const datum = new Date(`${tag}T12:00:00`)
  const feiertag = feiertagName(datum)
  /**
   * **Die Fenster dieses Tages** (AP 19.4) – das allgemeine, und je Arbeitswelt mit eigener
   * Zeit ein weiteres. Am Samstag bleibt dann nur das PRIVAT-Fenster übrig; arbeitsfrei ist
   * der Tag erst, wenn **gar keins** entsteht.
   */
  /**
   * **Und bin ich an diesem Tag überhaupt da?** (AP 19.5)
   *
   * Bis hierher rechnete der Plan den Urlaubstag wie jeden anderen durch – acht Stunden,
   * Grundlast ab, Aufgaben hinein. Er sah dabei aus wie jeder andere Plan, und das ist der
   * unangenehme Fall: Ein Plan, der sichtbar nichts weiß, lässt einen selber nachdenken; einer,
   * der glaubwürdig falsch liegt, nicht.
   *
   * Ein ganzer Abwesenheitstag läuft über **denselben Weg wie der Feiertag**: leeres Fenster,
   * Banner, Termine bleiben stehen. Ein halber kürzt jedes Fenster an seiner Mitte
   * ([`fensterKuerzen`](../../../../../packages/shared/src/arbeitszeit.ts)).
   */
  const abwesend = useMemo(
    () => tagesabwesenheit(meineAbwesenheiten, tag),
    [meineAbwesenheiten, tag],
  )
  const fenster = useMemo(
    () =>
      abwesend
        ? fensterKuerzen(fensterDesTages(zeitFelder, welten, datum), abwesend.verbleibt)
        : fensterDesTages(zeitFelder, welten, datum),
    [zeitFelder, welten, tag, abwesend],
  )
  const arbeitsfrei = fenster.length === 0

  /** Aufgabe → Pöple – `tasks.all` führt die Zugehörigkeit nicht mit, nur `pools.all`. */
  const poolsJeAufgabe = useMemo(() => {
    const karte = new Map<string, string[]>()
    for (const pool of allePools) {
      for (const a of pool.aufgaben ?? []) {
        const vorhanden = karte.get(a.id)
        if (vorhanden) vorhanden.push(pool.id)
        else karte.set(a.id, [pool.id])
      }
    }
    return karte
  }, [allePools])

  const plan = useMemo(() => {
    const umbauen = (a: (typeof aufgaben)[number]): PlanAufgabe => ({
      id: a.id,
      title: a.title,
      groesse: a.groesse ?? null,
      /**
       * **Ohne Minutenschätzung** – `tasks.all` führt sie nicht mit, und das bleibt so: Am
       * 30.09.2026 trug sie **1 von 117** offenen Aufgaben. Die Abfrage für alle zu
       * verbreitern, damit eine Zeile genauer wird, wäre der falsche Tausch. Die Dauer kommt
       * aus der Größe ([E-373](../../../../../docs/ENTSCHEIDUNGEN.md#e-373)).
       */
      priority: a.priority ?? null,
      dueAt: a.dueAt ?? null,
      updatedAt: a.updatedAt,
      workspaceId: a.workspaceId ?? null,
      /** Z-20: der wirksame Termin und das Alter – damit sortiert der Kalender wie der Vorlauf. */
      termin: termine.get(a.id) ?? null,
      sourceCreatedAt: a.sourceCreatedAt ?? null,
      createdAt: a.createdAt,
    })
    /**
     * **In Arbeit belegt, der Rest schlägt vor** (Nils, 30.09.2026: *„die Aufgaben waren noch
     * nicht verteilt"*).
     *
     * Angefangene Arbeit hat den Anspruch auf den Tag – sie steht in `aufgaben` und landet,
     * wenn sie nicht mehr passt, unter „Passt heute nicht mehr". Alles übrige Offene füllt als
     * **Vorschlag** die Restülücken und verschwindet stillschweigend, wenn der Tag voll ist.
     *
     * Beim Blättern auf einen anderen Tag ist **nichts** in Arbeit – dort besteht der Plan
     * ausschließlich aus Vorschlägen. Genau dafür blättert man: *„schaffe ich das bis
     * Freitag".*
     */
    /**
     * **Was an eine Uhrzeit gezogen wurde** (13.10.2026) – nur an seinem eigenen Tag. Liegt die
     * Markierung zurück, wandert der Tag mit, die Uhrzeit nicht: 10 Uhr von gestern ist heute
     * keine Verabredung mehr, und der Plan legt die Aufgabe wieder selbst.
     */
    const fest: FesteAufgabe[] = bestand.flatMap((a) => {
      const u = uhrzeiten.get(a.id)
      return u && u.am === tag && markierungen.has(a.id) ? [{ ...umbauen(a), um: u.um / 60 }] : []
    })
    const festIds = new Set(fest.map((a) => a.id))
    const laufend: PlanAufgabe[] = aufgaben
      .filter((a) => a.status === 'in_arbeit' && tag === heute && !festIds.has(a.id))
      .map(umbauen)
    /**
     * **Was ich mir für diesen Tag vorgenommen habe** (Z-20) – vor den Vorschlägen. Am heutigen
     * Tag ohne das Laufende, das steht schon oben; liegengeblieben zählt für heute (F-95).
     */
    const markiert: PlanAufgabe[] = bestand
      .filter((a) => {
        const am = markierungen.get(a.id)
        if (!am || wirksamerMarkierTag(am, heute) !== tag || festIds.has(a.id)) return false
        return !(tag === heute && a.status === 'in_arbeit')
      })
      .map(umbauen)
    const vorschlaege: PlanAufgabe[] = aufgaben
      .filter(
        (a) =>
          /** Markiertes ist kein Vorschlag mehr – weder heute noch an einem anderen Tag. */
          !markierungen.has(a.id) &&
          a.status !== 'in_arbeit' &&
          a.status !== 'erledigt' &&
          a.status !== 'review' &&
          a.status !== 'zurueckgestellt' &&
          /* Wartendes schläft bis zu seinem Tag (E-357) – dieselbe Grenze wie im Vorlauf. */
          a.status !== 'warten' &&
          /**
           * **Und nur, was der Vorlauf anbietet** ([E-394](../../../../../docs/ENTSCHEIDUNGEN.md#e-394)).
           * Eine Idee ohne Termin aus einem „nur mit Termin"-Pool ist kein Vorschlag – im
           * Vorlauf nicht, und hier deshalb auch nicht.
           */
          vorlaufBietetAn(
            {
              hatTermin: !!a.dueAt || !!a.zeitfenster,
              poolIds: poolsJeAufgabe.get(a.id) ?? [],
            },
            allePools,
            ichId,
          ),
      )
      .map(umbauen)
    /**
     * **An einem arbeitsfreien Tag wird nichts eingeplant**
     * ([Z-19](../../../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.7).
     *
     * Der Planer bekommt dann ein Fenster der Länge null und legt folglich nichts hinein –
     * weder Grundlast noch Aufgaben. **Termine bleiben sichtbar:** Sie kommen aus dem Kalender
     * und nicht aus der Arbeitszeit, und wer am 3. Oktober einen Eintrag hat, hat ihn wirklich.
     *
     * Das Raster darunter zeigt weiter 8–17 Uhr. Einen leeren Kasten ohne Stundenlinien zu
     * zeichnen wäre ehrlicher, aber beim Blättern springt dann die Höhe der ganzen Seite –
     * und der Satz oben sagt ohnehin, warum nichts drinsteht.
     */
    return tagBelegen(
      laufend,
      kalender?.termine ?? [],
      {
        fenster,
        grundlast: zeit.grundlast,
        mitEigenerZeit: new Set(welten.filter(hatEigeneZeit).map((w) => w.id)),
        /**
         * **Nur der heutige Tag kennt eine Uhr.** Blättert man nach vorn, steht der ganze Tag
         * zur Verfügung; blättert man zurück, ist er ganz vorbei und es wird gar nichts mehr
         * vorgeschlagen – beides fällt aus derselben Zeile.
         */
        ...(tag === heute
          ? { abStunde: new Date().getHours() + new Date().getMinutes() / 60 }
          : tag < heute
            ? { abStunde: 24 }
            : {}),
      },
      vorschlaege,
      weltdauern(welten),
      markiert,
      /** Heute mit der Uhr, ein anderer Tag mittags – „überfällig" gilt für den angezeigten Tag. */
      tag === heute ? new Date() : new Date(`${tag}T12:00:00`),
      fest,
    )
  }, [
    aufgaben,
    bestand,
    termine,
    markierungen,
    uhrzeiten,
    kalender,
    tag,
    heute,
    fenster,
    zeit.grundlast,
    welten,
    poolsJeAufgabe,
    allePools,
    ichId,
    takt,
  ])

  return {
    aufgaben,
    ichId,
    bestand,
    markierungen,
    uhrzeiten,
    termine,
    kalender,
    zeitFelder,
    welten,
    allePools,
    heute,
    zeit,
    meineAbwesenheiten,
    datum,
    feiertag,
    abwesend,
    fenster,
    arbeitsfrei,
    poolsJeAufgabe,
    plan,
  }
}
